import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FiX, FiEye, FiEyeOff } from "react-icons/fi";
import { useAuth } from "../context/AuthContext";
import { toast } from "react-toastify";

export default function Register() {
  const navigate = useNavigate();
  const { register, verifyRegistration } = useAuth();

  const [view, setView] = useState("register"); // "register" | "verify"
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleRegister = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    setLoading(true);
    try {
      await register(name, email, password);
      toast.success("Verification code sent to your email!");
      setView("verify");
    } catch (err) {
      const msg = err.response?.data?.message || "Registration failed";
      const errors = err.response?.data?.errors;
      if (errors) {
        Object.values(errors).flat().forEach((e) => toast.error(e));
      } else {
        toast.error(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await verifyRegistration(email, code);
      toast.success("Account verified! Welcome to The Slay Network!");
      navigate("/");
    } catch (err) {
      toast.error(err.response?.data?.message || "Invalid code. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-5 pt-24 pb-10 bg-dark-900 relative overflow-x-hidden">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[90vw] max-w-[600px] h-[400px] bg-primary/5 rounded-full blur-3xl" />

      <div className="w-full max-w-md bg-dark-700 border border-dark-400 rounded-2xl p-8 md:p-10 relative z-10 shadow-2xl">
        {/* Close (X) button */}
        <button
          onClick={() => navigate(-1)}
          className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 text-gray-400 hover:text-white transition-colors"
          aria-label="Close"
        >
          <FiX size={20} />
        </button>

        {/* ── REGISTER VIEW ── */}
        {view === "register" && (
          <>
            <h1 className="text-2xl font-bold text-center mb-2">Join The Slay Network</h1>
            <p className="text-center text-gray-500 text-sm mb-8">
              Create your free account and start streaming
            </p>

            <form onSubmit={handleRegister} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1.5">Full Name</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your full name"
                  required
                  className="w-full px-4 py-3 bg-dark-600 border border-dark-400 rounded-lg text-white text-sm placeholder:text-gray-600 focus:border-primary focus:ring-1 focus:ring-primary/30 outline-none transition-all"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1.5">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  className="w-full px-4 py-3 bg-dark-600 border border-dark-400 rounded-lg text-white text-sm placeholder:text-gray-600 focus:border-primary focus:ring-1 focus:ring-primary/30 outline-none transition-all"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1.5">Password</label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Min 8 characters"
                    required
                    minLength={8}
                    className="w-full px-4 py-3 pr-11 bg-dark-600 border border-dark-400 rounded-lg text-white text-sm placeholder:text-gray-600 focus:border-primary focus:ring-1 focus:ring-primary/30 outline-none transition-all"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 transition-colors"
                  >
                    {showPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1.5">Confirm Password</label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Re-enter your password"
                    required
                    className="w-full px-4 py-3 pr-11 bg-dark-600 border border-dark-400 rounded-lg text-white text-sm placeholder:text-gray-600 focus:border-primary focus:ring-1 focus:ring-primary/30 outline-none transition-all"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 transition-colors"
                  >
                    {showConfirmPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full btn-play-gradient disabled:opacity-50"
              >
                {loading ? "Creating account..." : "Create Account"}
              </button>
            </form>

            <p className="text-center text-sm text-gray-500 mt-6">
              Already have an account?{" "}
              <Link to="/login" className="text-primary font-semibold hover:text-primary-hover">
                Sign in
              </Link>
            </p>
          </>
        )}

        {/* ── VERIFICATION VIEW ── */}
        {view === "verify" && (
          <>
            <h1 className="text-2xl font-bold text-center mb-2">Verify Your Account</h1>
            <p className="text-center text-gray-500 text-sm mb-8">
              Enter the 6-digit code sent to <span className="text-white">{email}</span>
            </p>

            <form onSubmit={handleVerify} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1.5">Confirmation Code</label>
                <input
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  placeholder="000000"
                  required
                  maxLength={6}
                  className="w-full px-4 py-4 bg-dark-600 border border-dark-400 rounded-lg text-white text-center tracking-[0.5em] font-mono text-xl placeholder:tracking-[0.5em] placeholder:text-gray-600 focus:border-primary focus:ring-1 focus:ring-primary/30 outline-none transition-all"
                />
              </div>

              <button
                type="submit"
                disabled={loading || code.length < 6}
                className="w-full btn-play-gradient disabled:opacity-50"
              >
                {loading ? "Verifying..." : "Finish"}
              </button>
            </form>

            <p className="text-center text-sm text-gray-500 mt-5">
              Didn't receive the code?{" "}
              <button onClick={handleRegister} className="text-primary font-medium hover:text-primary-hover">
                Resend
              </button>
            </p>
          </>
        )}
      </div>
    </div>
  );
}
